import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  Badge,
  Button,
  EmptyState,
  Logo,
  formatMoney,
  orderTone,
} from "@globaldine/ui";
import {
  bestLocale,
  createTranslator,
  localeLabels,
  statusLabel,
} from "@globaldine/i18n";
import {
  cartItemUnitPrice,
  convertCurrency,
  localize,
  supportedLocales,
  type CartModifier,
  type CustomerUser,
  type ExchangeRates,
  type Locale,
  type MenuItem,
  type Order,
  type QueueDisplayState,
  type QueueTicket,
  type SharedCart,
  type Store,
} from "@globaldine/contracts";
import {
  BellRing,
  Check,
  ChevronLeft,
  Clock3,
  Languages,
  ListOrdered,
  LocateFixed,
  LogIn,
  MapPin,
  MessageSquareText,
  Minus,
  Plus,
  ReceiptText,
  ShoppingBag,
  Star,
  Store as StoreIcon,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { API_URL, api } from "./api";

type View = "entry" | "menu" | "orders" | "queue";
type Bootstrap = {
  store: Store;
  rates: ExchangeRates;
  entry: {
    tableId?: string;
    tableSessionId?: string;
    scanSource: string;
    mode: "ordering" | "queue" | "queue_display";
    fulfillmentMode?: "dine_in" | "takeaway";
    presenceToken?: string;
    staffPresenceCodeEnabled?: boolean;
  };
  runtime: { demo: boolean };
};

const currencyForLocale: Record<Locale, string> = {
  vi: "VND",
  "zh-Hans": "CNY",
  "zh-Hant": "CNY",
  en: "USD",
  ko: "KRW",
  ru: "RUB",
  ja: "JPY",
  th: "THB",
};

const speechLanguages: Record<Locale, string> = {
  vi: "vi-VN",
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW",
  en: "en-US",
  ko: "ko-KR",
  ru: "ru-RU",
  ja: "ja-JP",
  th: "th-TH",
};
const queueSpeech = (number: string, locale: Locale): string =>
  (
    ({
      vi: `${number}, mời quý khách vào dùng bữa`,
      "zh-Hans": `${number}，请您入座用餐`,
      "zh-Hant": `${number}，請您入座用餐`,
      en: `${number}, your table is ready. Please come in`,
      ko: `${number}, 자리가 준비되었습니다. 입장해 주세요`,
      ru: `${number}, ваш стол готов. Пожалуйста, проходите`,
      ja: `${number}、お席の準備ができました。どうぞお入りください`,
      th: `${number} โต๊ะพร้อมแล้ว เชิญด้านในค่ะ`,
    }) satisfies Record<Locale, string>
  )[locale];

function preferredVoice(locale: Locale) {
  const target = speechLanguages[locale].toLowerCase();
  return window.speechSynthesis
    .getVoices()
    .filter((voice) =>
      voice.lang.toLowerCase().startsWith(target.split("-")[0]!),
    )
    .sort((a, b) => {
      const score = (voice: SpeechSynthesisVoice) =>
        (voice.lang.toLowerCase() === target ? 100 : 0) +
        (/natural|premium|enhanced|neural|online/i.test(voice.name) ? 100 : 0) +
        (!voice.localService ? 60 : 0) +
        (/microsoft|google|apple|siri/i.test(voice.name) ? 30 : 0) +
        (/xiaoxiao|xiaoyi|yunxia|huihui|tingting|meijia|samantha|zira|aria|jenny|female/i.test(
          voice.name,
        )
          ? 15
          : 0) -
        (/compact|espeak|festival|default/i.test(voice.name) ? 60 : 0);
      return score(b) - score(a);
    })[0];
}

function announceQueueNumber(
  number: string,
  locale: Locale,
  repeats: number,
  intervalMs: number,
) {
  if (!("speechSynthesis" in window)) return () => undefined;
  let cancelled = false;
  let timer: number | undefined;
  window.speechSynthesis.cancel();
  const speak = (index: number) => {
    if (cancelled || index >= repeats) return;
    const utterance = new SpeechSynthesisUtterance(queueSpeech(number, locale));
    utterance.lang = speechLanguages[locale];
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    const voice = preferredVoice(locale);
    if (voice) utterance.voice = voice;
    let continued = false;
    const continuePlayback = () => {
      if (continued) return;
      continued = true;
      if (!cancelled && index + 1 < repeats)
        timer = window.setTimeout(() => speak(index + 1), intervalMs);
    };
    utterance.onend = continuePlayback;
    utterance.onerror = continuePlayback;
    window.speechSynthesis.speak(utterance);
  };
  timer = window.setTimeout(
    () => speak(0),
    window.speechSynthesis.getVoices().length ? 0 : 250,
  );
  return () => {
    cancelled = true;
    if (timer !== undefined) window.clearTimeout(timer);
    window.speechSynthesis.cancel();
  };
}

function unlockQueueSpeech() {
  if (!("speechSynthesis" in window)) return;
  const utterance = new SpeechSynthesisUtterance(" ");
  utterance.volume = 0;
  window.speechSynthesis.speak(utterance);
}
const statusIndex: Record<string, number> = {
  awaiting_confirmation: 0,
  confirmed: 1,
  preparing: 2,
  ready: 3,
  served: 4,
  completed: 4,
};
const customerAuthCopy: Record<
  Locale,
  {
    login: string;
    register: string;
    account: string;
    identifier: string;
    password: string;
    name: string;
    logout: string;
    loginToReview: string;
  }
> = {
  vi: {
    login: "Đăng nhập",
    register: "Đăng ký",
    account: "Tài khoản",
    identifier: "Số điện thoại hoặc email",
    password: "Mật khẩu",
    name: "Tên hiển thị",
    logout: "Đăng xuất",
    loginToReview: "Đăng nhập để đánh giá và lưu đơn hàng",
  },
  "zh-Hans": {
    login: "登录",
    register: "注册",
    account: "用户账号",
    identifier: "手机号或邮箱",
    password: "密码",
    name: "昵称",
    logout: "退出",
    loginToReview: "登录后可评价并永久保存订单记录",
  },
  "zh-Hant": {
    login: "登入",
    register: "註冊",
    account: "使用者帳號",
    identifier: "手機號或信箱",
    password: "密碼",
    name: "暱稱",
    logout: "登出",
    loginToReview: "登入後可評價並永久儲存訂單記錄",
  },
  en: {
    login: "Sign in",
    register: "Register",
    account: "Account",
    identifier: "Phone or email",
    password: "Password",
    name: "Display name",
    logout: "Sign out",
    loginToReview: "Sign in to review and save order history",
  },
  ko: {
    login: "로그인",
    register: "가입",
    account: "계정",
    identifier: "전화번호 또는 이메일",
    password: "비밀번호",
    name: "표시 이름",
    logout: "로그아웃",
    loginToReview: "로그인하면 리뷰와 주문 내역을 저장할 수 있습니다",
  },
  ru: {
    login: "Войти",
    register: "Регистрация",
    account: "Аккаунт",
    identifier: "Телефон или email",
    password: "Пароль",
    name: "Имя",
    logout: "Выйти",
    loginToReview: "Войдите, чтобы оставить отзыв и сохранить заказы",
  },
  ja: {
    login: "ログイン",
    register: "登録",
    account: "アカウント",
    identifier: "電話番号またはメール",
    password: "パスワード",
    name: "表示名",
    logout: "ログアウト",
    loginToReview: "ログインすると評価と注文履歴を保存できます",
  },
  th: {
    login: "เข้าสู่ระบบ",
    register: "สมัครสมาชิก",
    account: "บัญชี",
    identifier: "โทรศัพท์หรืออีเมล",
    password: "รหัสผ่าน",
    name: "ชื่อที่แสดง",
    logout: "ออกจากระบบ",
    loginToReview: "เข้าสู่ระบบเพื่อรีวิวและบันทึกประวัติคำสั่งซื้อ",
  },
};

const queueCopy: Record<
  Locale,
  {
    title: string;
    now: string;
    waiting: string;
    parties: string;
    welcome: string;
    choose: string;
    dine: string;
    dineHint: string;
    take: string;
    takeHint: string;
  }
> = {
  vi: {
    title: "Màn hình gọi số",
    now: "Đang gọi",
    waiting: "Đang chờ",
    parties: "nhóm",
    welcome: "Chào mừng",
    choose: "Vui lòng chọn dịch vụ",
    dine: "Lấy số dùng tại chỗ",
    dineHint: "Nhận số Q và chờ xếp bàn",
    take: "Đặt món mang đi",
    takeHint: "Chọn món ngay, thu ngân sẽ xác nhận",
  },
  "zh-Hans": {
    title: "排队叫号",
    now: "当前叫号",
    waiting: "等待中",
    parties: "桌",
    welcome: "欢迎光临",
    choose: "请选择服务方式",
    dine: "堂食取号",
    dineHint: "领取 Q 号，等待安排桌台",
    take: "外带点餐",
    takeHint: "直接选餐提交，由收银台审核",
  },
  "zh-Hant": {
    title: "排隊叫號",
    now: "目前叫號",
    waiting: "等候中",
    parties: "桌",
    welcome: "歡迎光臨",
    choose: "請選擇服務方式",
    dine: "堂食取號",
    dineHint: "領取 Q 號，等候安排桌台",
    take: "外帶點餐",
    takeHint: "直接選餐提交，由收銀台審核",
  },
  en: {
    title: "Waitlist calling",
    now: "Now calling",
    waiting: "Waiting",
    parties: "parties",
    welcome: "Welcome",
    choose: "Choose a service",
    dine: "Dine-in number",
    dineHint: "Get a Q number and wait for a table",
    take: "Takeaway order",
    takeHint: "Order now for cashier approval",
  },
  ko: {
    title: "대기 번호 호출",
    now: "현재 호출",
    waiting: "대기 중",
    parties: "팀",
    welcome: "환영합니다",
    choose: "서비스를 선택하세요",
    dine: "매장 식사 번호표",
    dineHint: "Q 번호를 받고 테이블을 기다리세요",
    take: "포장 주문",
    takeHint: "바로 주문하고 계산대 승인을 받으세요",
  },
  ru: {
    title: "Экран очереди",
    now: "Сейчас вызывается",
    waiting: "Ожидают",
    parties: "групп",
    welcome: "Добро пожаловать",
    choose: "Выберите услугу",
    dine: "Очередь на стол",
    dineHint: "Получите номер Q и ожидайте стол",
    take: "Заказ с собой",
    takeHint: "Закажите сейчас, кассир подтвердит",
  },
  ja: {
    title: "順番待ち案内",
    now: "お呼び出し中",
    waiting: "待機中",
    parties: "組",
    welcome: "いらっしゃいませ",
    choose: "サービスを選択してください",
    dine: "店内利用の受付",
    dineHint: "Q番号を取り、席の案内をお待ちください",
    take: "持ち帰り注文",
    takeHint: "すぐに注文し、レジで確認します",
  },
  th: {
    title: "จอเรียกคิว",
    now: "กำลังเรียก",
    waiting: "กำลังรอ",
    parties: "กลุ่ม",
    welcome: "ยินดีต้อนรับ",
    choose: "เลือกบริการ",
    dine: "รับคิวทานที่ร้าน",
    dineHint: "รับหมายเลข Q และรอโต๊ะ",
    take: "สั่งกลับบ้าน",
    takeHint: "สั่งได้ทันทีและรอแคชเชียร์อนุมัติ",
  },
};

const waitPageCopy: Record<
  Locale,
  {
    yourNumber: string;
    current: string;
    ahead: string;
    tables: string;
    estimate: string;
    minutes: string;
    partySize: string;
    getNumber: string;
    preorder: string;
    preorderHint: string;
  }
> = {
  vi: {
    yourNumber: "Số của bạn",
    current: "Đang gọi",
    ahead: "Trước bạn",
    tables: "bàn",
    estimate: "Thời gian chờ dự kiến",
    minutes: "phút",
    partySize: "Số khách",
    getNumber: "Lấy số",
    preorder: "Đặt món trước",
    preorderHint: "Đặt món ngay, quét mã bàn khi được xếp chỗ",
  },
  "zh-Hans": {
    yourNumber: "你的排队号码",
    current: "当前叫号",
    ahead: "你前面还有",
    tables: "桌",
    estimate: "预计还要等待",
    minutes: "分钟",
    partySize: "用餐人数",
    getNumber: "立即取号",
    preorder: "预点餐",
    preorderHint: "可提前提交，入座后扫描桌台码绑定",
  },
  "zh-Hant": {
    yourNumber: "你的排隊號碼",
    current: "目前叫號",
    ahead: "你前面還有",
    tables: "桌",
    estimate: "預計還要等候",
    minutes: "分鐘",
    partySize: "用餐人數",
    getNumber: "立即取號",
    preorder: "預點餐",
    preorderHint: "可提前提交，入座後掃描桌台碼綁定",
  },
  en: {
    yourNumber: "Your waitlist number",
    current: "Now calling",
    ahead: "Ahead of you",
    tables: "parties",
    estimate: "Estimated wait",
    minutes: "min",
    partySize: "Party size",
    getNumber: "Get a number",
    preorder: "Pre-order",
    preorderHint: "Order now, then scan the table QR when seated",
  },
  ko: {
    yourNumber: "내 대기 번호",
    current: "현재 호출",
    ahead: "앞 대기",
    tables: "팀",
    estimate: "예상 대기",
    minutes: "분",
    partySize: "인원",
    getNumber: "번호 받기",
    preorder: "미리 주문",
    preorderHint: "미리 주문하고 착석 후 테이블 QR을 스캔하세요",
  },
  ru: {
    yourNumber: "Ваш номер",
    current: "Сейчас вызывается",
    ahead: "Перед вами",
    tables: "групп",
    estimate: "Примерное ожидание",
    minutes: "мин",
    partySize: "Гостей",
    getNumber: "Получить номер",
    preorder: "Предзаказ",
    preorderHint: "Закажите заранее и привяжите стол по QR после посадки",
  },
  ja: {
    yourNumber: "あなたの受付番号",
    current: "お呼び出し中",
    ahead: "前の組数",
    tables: "組",
    estimate: "予想待ち時間",
    minutes: "分",
    partySize: "人数",
    getNumber: "番号を取る",
    preorder: "事前注文",
    preorderHint: "先に注文し、着席後にテーブルQRを読み取ってください",
  },
  th: {
    yourNumber: "หมายเลขคิวของคุณ",
    current: "กำลังเรียก",
    ahead: "ก่อนหน้าคุณ",
    tables: "กลุ่ม",
    estimate: "เวลารอโดยประมาณ",
    minutes: "นาที",
    partySize: "จำนวนคน",
    getNumber: "รับคิว",
    preorder: "สั่งล่วงหน้า",
    preorderHint: "สั่งก่อน แล้วสแกน QR โต๊ะเมื่อนั่งแล้ว",
  },
};

const queueExtraCopy: Record<
  Locale,
  { custom: string; missed: string; takeAgain: string }
> = {
  vi: {
    custom: "Tùy chỉnh",
    missed: "Đã quá lượt, vui lòng lấy số mới",
    takeAgain: "Lấy số mới",
  },
  "zh-Hans": {
    custom: "自定义人数",
    missed: "已过号，请重新取号",
    takeAgain: "重新取号",
  },
  "zh-Hant": {
    custom: "自訂人數",
    missed: "已過號，請重新取號",
    takeAgain: "重新取號",
  },
  en: {
    custom: "Custom party size",
    missed: "Your number was missed. Please take a new number.",
    takeAgain: "Take a new number",
  },
  ko: {
    custom: "인원 직접 입력",
    missed: "호출 순서가 지났습니다. 새 번호를 받아 주세요.",
    takeAgain: "새 번호 받기",
  },
  ru: {
    custom: "Другое количество",
    missed: "Ваш номер пропущен. Получите новый номер.",
    takeAgain: "Получить новый номер",
  },
  ja: {
    custom: "人数を入力",
    missed: "呼出済みです。新しい番号を取得してください。",
    takeAgain: "番号を取り直す",
  },
  th: {
    custom: "ระบุจำนวนเอง",
    missed: "เลยคิวแล้ว กรุณารับหมายเลขใหม่",
    takeAgain: "รับหมายเลขใหม่",
  },
};

function QueueBoard({ bootstrap }: { bootstrap: Bootstrap }) {
  const [data, setData] = useState<QueueDisplayState>();
  const announcedRef = useRef("");
  const languages = bootstrap.store.languages.length
    ? bootstrap.store.languages
    : ["en" as Locale];
  const [languageIndex, setLanguageIndex] = useState(0);
  const refresh = () =>
    api<QueueDisplayState>(
      `/api/queue/display?store=${bootstrap.store.slug}`,
    ).then(setData);
  useEffect(() => {
    void refresh();
    const socket = io(API_URL);
    socket.emit("subscribe", [`queue-display:${bootstrap.store.id}`]);
    socket.on("queue:update", refresh);
    const timer = setInterval(refresh, 3000);
    return () => {
      clearInterval(timer);
      socket.close();
    };
  }, [bootstrap.store.id]);
  useEffect(() => {
    const timer = setInterval(
      () => setLanguageIndex((value) => (value + 1) % languages.length),
      7000,
    );
    return () => clearInterval(timer);
  }, [languages.length]);
  useEffect(() => {
    const ticket = data?.calledTickets?.[0];
    const voice = bootstrap.store.queueVoice ?? {
      enabled: true,
      repeats: 3,
      intervalMs: 1800,
    };
    if (
      !ticket ||
      !voice.enabled ||
      announcedRef.current === `${ticket.id}:${ticket.calledAt}` ||
      !("speechSynthesis" in window)
    )
      return;
    announcedRef.current = `${ticket.id}:${ticket.calledAt}`;
    return announceQueueNumber(
      ticket.number,
      ticket.customerLocale,
      voice.repeats,
      voice.intervalMs,
    );
  }, [
    bootstrap.store.queueVoice?.enabled,
    bootstrap.store.queueVoice?.intervalMs,
    bootstrap.store.queueVoice?.repeats,
    data?.calledTickets?.[0]?.calledAt,
    data?.calledTickets?.[0]?.customerLocale,
    data?.calledTickets?.[0]?.id,
    data?.calledTickets?.[0]?.number,
  ]);
  const activeLocale = languages[languageIndex % languages.length]!;
  const copy = queueCopy[activeLocale];
  return (
    <div className="queue-board">
      <div className="queue-board-top">
        <Logo />
        <div>{localize(bootstrap.store.name, activeLocale)}</div>
        <span>{localeLabels[activeLocale]}</span>
      </div>
      <main>
        <p className="queue-board-kicker">{copy.title}</p>
        <section className="queue-now">
          <span>{copy.now}</span>
          <strong>{data?.calledNumbers[0] ?? "—"}</strong>
        </section>
        <section className="queue-waiting">
          <span>{copy.waiting}</span>
          <strong>{data?.waitingCount ?? 0}</strong>
          <small>{copy.parties}</small>
        </section>
        <div className="queue-recent">
          {data?.calledNumbers.slice(1).map((number) => (
            <span key={number}>{number}</span>
          ))}
        </div>
      </main>
      <div className="queue-marquee">
        <span>
          {languages
            .map(
              (code) =>
                `${queueCopy[code].now}: ${data?.calledNumbers[0] ?? "—"} · ${queueCopy[code].waiting}: ${data?.waitingCount ?? 0} ${queueCopy[code].parties}`,
            )
            .join("　　◆　　")}
        </span>
      </div>
    </div>
  );
}

function identity() {
  const saved = localStorage.getItem("globaldine:diner");
  if (saved) return JSON.parse(saved) as { id: string; name: string };
  const value = {
    id: crypto.randomUUID(),
    name: `Guest ${Math.floor(100 + Math.random() * 900)}`,
  };
  localStorage.setItem("globaldine:diner", JSON.stringify(value));
  return value;
}

export function App() {
  const [bootstrap, setBootstrap] = useState<Bootstrap>();
  const [locale, setLocale] = useState<Locale>(() =>
    bestLocale(navigator.language),
  );
  const [currency, setCurrency] = useState(
    () => currencyForLocale[bestLocale(navigator.language)],
  );
  const [diner] = useState(identity);
  const [cart, setCart] = useState<SharedCart>();
  const [orders, setOrders] = useState<Order[]>([]);
  const [view, setView] = useState<View>("menu");
  const [preorderStarted, setPreorderStarted] = useState(false);
  const [category, setCategory] = useState<string>("all");
  const [selected, setSelected] = useState<MenuItem>();
  const [selectedModifiers, setSelectedModifiers] = useState<CartModifier[]>(
    [],
  );
  const [notes, setNotes] = useState("");
  const [fulfillment, setFulfillment] = useState<"dine_in" | "takeaway">(
    "dine_in",
  );
  const [cartMobile, setCartMobile] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [presenceToken, setPresenceToken] = useState<string>();
  const [staffCode, setStaffCode] = useState("");
  const [locationMessage, setLocationMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [queueTicket, setQueueTicket] = useState<QueueTicket>();
  const [queueDisplay, setQueueDisplay] = useState<QueueDisplayState>();
  const [partySize, setPartySize] = useState(2);
  const [reviewOpen, setReviewOpen] = useState<Order>();
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [customerToken, setCustomerToken] = useState(
    () => localStorage.getItem("globaldine:customer-token") ?? "",
  );
  const [customer, setCustomer] = useState<CustomerUser>();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [authForm, setAuthForm] = useState({
    identifier: "",
    password: "",
    displayName: "",
  });
  const historyReady = useRef(false);
  const customerAnnouncedRef = useRef("");
  const t = createTranslator(locale);

  const query = useMemo(() => new URLSearchParams(location.search), []);
  const storeSlug = query.get("store") ?? "lotus-pho";
  const specialEntry =
    query.get("queue") === "1" || query.get("display") === "queue";
  const defaultTable =
    specialEntry || storeSlug === "orbit-tea"
      ? undefined
      : (query.get("table") ?? "T08");
  const defaultSession = defaultTable
    ? (query.get("session") ?? `demo-${defaultTable}`)
    : undefined;

  useEffect(() => {
    const params = new URLSearchParams({
      store: storeSlug,
      source: query.get("source") ?? "browser",
    });
    if (defaultTable) params.set("table", defaultTable);
    if (defaultSession) params.set("session", defaultSession);
    if (query.get("queue") === "1") params.set("queue", "1");
    if (query.get("display") === "queue") params.set("display", "queue");
    if (query.get("counter") === "1") params.set("counter", "1");
    if (query.get("presence")) params.set("presence", query.get("presence")!);
    api<Bootstrap>(`/api/bootstrap?${params}`)
      .then((result) => {
        setBootstrap(result);
        if (result.entry.mode === "queue") setView("entry");
      })
      .catch((error) => setToast(error.message));
  }, [defaultSession, defaultTable, query, storeSlug]);

  useEffect(() => {
    if (!bootstrap) return;
    if (bootstrap.entry.presenceToken)
      setPresenceToken(bootstrap.entry.presenceToken);
    if (bootstrap.entry.mode === "queue_display") return;
    api<QueueTicket>(
      `/api/queue/mine?storeId=${bootstrap.store.id}&dinerId=${diner.id}`,
    )
      .then((ticket) => {
        setQueueTicket(ticket);
        setPresenceToken((current) => current ?? "queue-ticket-proof");
      })
      .catch(() => undefined);
    if (bootstrap.entry.mode === "queue") {
      if (fulfillment !== "takeaway") setFulfillment("dine_in");
    } else {
      setFulfillment(
        bootstrap.entry.fulfillmentMode ??
          (bootstrap.store.type === "drink" ? "takeaway" : "dine_in"),
      );
    }
    if (!bootstrap.store.fulfillmentModes.includes(fulfillment))
      setFulfillment(bootstrap.store.fulfillmentModes[0] ?? "dine_in");
    api<SharedCart>("/api/carts/join", {
      method: "POST",
      body: JSON.stringify({
        storeId: bootstrap.store.id,
        tableId: bootstrap.entry.tableId,
        tableSessionId: bootstrap.entry.tableSessionId,
        dinerId: diner.id,
        dinerName: diner.name,
        locale,
        currency,
      }),
    })
      .then(async (joined) => {
        setCart(joined);
        const found = await api<Order[]>(`/api/orders?cartId=${joined.id}`);
        setOrders((current) => [
          ...found,
          ...current.filter(
            (item) => !found.some((saved) => saved.id === item.id),
          ),
        ]);
      })
      .catch((error) => setToast(error.message));
  }, [bootstrap, currency, diner.id, diner.name, fulfillment, locale]);

  useEffect(() => {
    if (!bootstrap || !cart) return;
    const socket = io(API_URL);
    socket.emit("subscribe", [
      `cart:${cart.id}`,
      `queue-display:${bootstrap.store.id}`,
      ...orders.map((order) => `order:${order.id}`),
    ]);
    socket.on(
      "cart:update",
      (next: SharedCart) => next.id === cart.id && setCart(next),
    );
    const updateOrder = (next: Order) =>
      setOrders((current) => [
        next,
        ...current.filter((order) => order.id !== next.id),
      ]);
    socket.on("order:new", updateOrder);
    socket.on("order:update", updateOrder);
    socket.on("menu:update", (menu: MenuItem[]) =>
      setBootstrap((current) =>
        current ? { ...current, store: { ...current.store, menu } } : current,
      ),
    );
    socket.on("queue:update", (tickets: QueueTicket[]) => {
      const mine = tickets
        .filter(
          (ticket) =>
            ticket.dinerId === diner.id &&
            ["waiting", "called", "skipped"].includes(ticket.status),
        )
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      if (mine) setQueueTicket(mine);
      else if (
        tickets.some(
          (ticket) => ticket.dinerId === diner.id && ticket.status === "seated",
        )
      )
        setQueueTicket(undefined);
    });
    return () => {
      socket.close();
    };
  }, [bootstrap?.store.id, cart?.id, orders.length]);

  useEffect(() => {
    if (!bootstrap?.entry.tableId || !bootstrap.entry.tableSessionId) return;
    const orderId = localStorage.getItem(
      `globaldine:preorder:${bootstrap.store.id}`,
    );
    if (!orderId) return;
    api<Order>(`/api/orders/${orderId}/bind-table`, {
      method: "POST",
      body: JSON.stringify({
        dinerId: diner.id,
        tableId: bootstrap.entry.tableId,
        tableSessionId: bootstrap.entry.tableSessionId,
      }),
    })
      .then((order) => {
        setOrders((current) => [
          order,
          ...current.filter((item) => item.id !== order.id),
        ]);
        localStorage.removeItem(`globaldine:preorder:${bootstrap.store.id}`);
        setToast(statusLabel("seated", locale));
      })
      .catch(() => undefined);
  }, [
    bootstrap?.entry.tableId,
    bootstrap?.entry.tableSessionId,
    bootstrap?.store.id,
    diner.id,
    locale,
  ]);

  useEffect(() => {
    if (bootstrap?.entry.mode !== "queue") return;
    const refresh = () =>
      api<QueueDisplayState>(
        `/api/queue/display?store=${bootstrap.store.slug}`,
      ).then(setQueueDisplay);
    void refresh();
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [bootstrap?.entry.mode, bootstrap?.store.slug]);

  useEffect(() => {
    if (
      !bootstrap ||
      bootstrap.entry.mode !== "queue" ||
      queueTicket?.status !== "called"
    )
      return;
    const announcementKey = `${queueTicket.id}:${queueTicket.calledAt ?? queueTicket.createdAt}`;
    if (customerAnnouncedRef.current === announcementKey) return;
    customerAnnouncedRef.current = announcementKey;
    const voice = bootstrap.store.queueVoice ?? {
      enabled: true,
      repeats: 3,
      intervalMs: 1800,
    };
    if (!voice.enabled) return;
    return announceQueueNumber(
      queueTicket.number,
      queueTicket.customerLocale ?? locale,
      voice.repeats,
      voice.intervalMs,
    );
  }, [
    bootstrap?.entry.mode,
    bootstrap?.store.queueVoice?.enabled,
    bootstrap?.store.queueVoice?.intervalMs,
    bootstrap?.store.queueVoice?.repeats,
    locale,
    queueTicket?.calledAt,
    queueTicket?.createdAt,
    queueTicket?.customerLocale,
    queueTicket?.id,
    queueTicket?.number,
    queueTicket?.status,
  ]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!customerToken) return setCustomer(undefined);
    const headers = { Authorization: `Bearer ${customerToken}` };
    api<CustomerUser>("/api/customer/me", { headers })
      .then(async (user) => {
        setCustomer(user);
        await api("/api/customer/claim", {
          method: "POST",
          headers,
          body: JSON.stringify({ dinerId: diner.id }),
        });
        const history = await api<Order[]>("/api/customer/orders", { headers });
        setOrders((current) => [
          ...history,
          ...current.filter(
            (item) => !history.some((saved) => saved.id === item.id),
          ),
        ]);
      })
      .catch(() => {
        localStorage.removeItem("globaldine:customer-token");
        setCustomerToken("");
      });
  }, [customerToken, diner.id]);

  useEffect(() => {
    if (!historyReady.current) {
      history.replaceState({ globaldine: true }, "");
      history.pushState({ globaldine: true }, "");
      historyReady.current = true;
      return;
    }
    history.pushState({ globaldine: true, view, preorderStarted }, "");
  }, [view, preorderStarted]);

  useEffect(() => {
    const onBack = () => {
      if (selected) return setSelected(undefined);
      if (checkoutOpen) return setCheckoutOpen(false);
      if (reviewOpen) return setReviewOpen(undefined);
      if (authOpen) return setAuthOpen(false);
      if (cartMobile) return setCartMobile(false);
      if (preorderStarted) return setPreorderStarted(false);
      if (view !== "menu") return setView("menu");
      history.pushState({ globaldine: true }, "");
    };
    addEventListener("popstate", onBack);
    return () => removeEventListener("popstate", onBack);
  }, [
    selected,
    checkoutOpen,
    reviewOpen,
    authOpen,
    cartMobile,
    preorderStarted,
    view,
  ]);

  const store = bootstrap?.store;
  const menu =
    store?.menu.filter(
      (item) => category === "all" || item.categoryId === category,
    ) ?? [];
  const cartTotal =
    cart && store
      ? cart.items.reduce((sum, item) => {
          const dish = store.menu.find(
            (candidate) => candidate.id === item.menuItemId,
          );
          return (
            sum + (dish ? cartItemUnitPrice(dish, item) * item.quantity : 0)
          );
        }, 0)
      : 0;
  const approximate = bootstrap
    ? convertCurrency(cartTotal, bootstrap.rates, currency)
    : 0;
  const activeOrder = orders.find(
    (order) => !["completed", "cancelled"].includes(order.status),
  );

  function changeLocale(value: Locale) {
    setLocale(value);
    setCurrency(currencyForLocale[value]);
    localStorage.setItem("globaldine:locale", value);
  }

  async function submitCustomerAuth() {
    setBusy(true);
    try {
      const result = await api<{ token: string; user: CustomerUser }>(
        `/api/customer/${authMode}`,
        {
          method: "POST",
          body: JSON.stringify({ ...authForm, preferredLanguage: locale }),
        },
      );
      localStorage.setItem("globaldine:customer-token", result.token);
      setCustomerToken(result.token);
      setCustomer(result.user);
      setAuthOpen(false);
    } catch (error) {
      setToast((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function customerSignOut() {
    localStorage.removeItem("globaldine:customer-token");
    setCustomerToken("");
    setCustomer(undefined);
  }

  function openDish(item: MenuItem) {
    if (!item.available) return;
    setSelected(item);
    setNotes("");
    setSelectedModifiers(
      item.modifierGroups.flatMap((group) => {
        const first = group.required
          ? group.options.find((option) => option.available)
          : undefined;
        return first
          ? [
              {
                groupId: group.id,
                optionId: first.id,
                name: first.name,
                priceDelta: first.priceDelta,
              },
            ]
          : [];
      }),
    );
  }

  function chooseModifier(item: MenuItem, groupId: string, optionId: string) {
    const group = item.modifierGroups.find(
      (candidate) => candidate.id === groupId,
    )!;
    const option = group.options.find(
      (candidate) => candidate.id === optionId,
    )!;
    setSelectedModifiers((current) => [
      ...current.filter((modifier) => modifier.groupId !== groupId),
      { groupId, optionId, name: option.name, priceDelta: option.priceDelta },
    ]);
  }

  async function addDish() {
    if (!selected || !cart) return;
    setBusy(true);
    try {
      const next = await api<SharedCart>(`/api/carts/${cart.id}/items`, {
        method: "POST",
        body: JSON.stringify({
          menuItemId: selected.id,
          dinerId: diner.id,
          dinerName: diner.name,
          quantity: 1,
          notes,
          fulfillmentMode: fulfillment,
          modifiers: selectedModifiers,
        }),
      });
      setCart(next);
      setSelected(undefined);
      setToast(`${localize(selected.name, locale)} · ${t("add")}`);
    } catch (error) {
      setToast((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function changeQuantity(itemId: string, quantity: number) {
    if (!cart) return;
    if (quantity <= 0) {
      const next = await api<SharedCart>(
        `/api/carts/${cart.id}/items/${itemId}?actorId=${diner.id}`,
        { method: "DELETE" },
      );
      return setCart(next);
    }
    const next = await api<SharedCart>(
      `/api/carts/${cart.id}/items/${itemId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ actorId: diner.id, quantity }),
      },
    );
    setCart(next);
  }

  async function verifyCoordinates(
    latitude: number,
    longitude: number,
    accuracy: number,
  ) {
    if (!store) return;
    setLocationMessage(
      locale.startsWith("zh") ? "正在验证位置…" : "Verifying location…",
    );
    try {
      const result = await api<{ token: string; distanceMeters: number }>(
        "/api/location/verify",
        {
          method: "POST",
          body: JSON.stringify({
            storeId: store.id,
            latitude,
            longitude,
            accuracy,
            tableSessionId: cart?.tableSessionId,
          }),
        },
      );
      setPresenceToken(result.token);
      setLocationMessage(
        locale.startsWith("zh")
          ? `已验证到店（约 ${result.distanceMeters} 米）`
          : `Verified at venue · ${result.distanceMeters}m`,
      );
    } catch (error) {
      const detail = (
        error as Error & { body?: { error?: string; distanceMeters?: number } }
      ).body;
      setLocationMessage(
        detail?.error === "OUTSIDE_GEOFENCE"
          ? locale.startsWith("zh")
            ? `距离餐厅约 ${detail.distanceMeters} 米，请使用店员码`
            : `You appear ${detail.distanceMeters}m away. Ask staff for a code.`
          : locale.startsWith("zh")
            ? "定位精度不足，请使用店员码"
            : "Location unavailable. Ask staff for a code.",
      );
    }
  }

  function verifyLocation() {
    if (!navigator.geolocation)
      return setLocationMessage("Geolocation unavailable");
    navigator.geolocation.getCurrentPosition(
      (position) =>
        verifyCoordinates(
          position.coords.latitude,
          position.coords.longitude,
          position.coords.accuracy,
        ),
      () =>
        setLocationMessage(
          locale.startsWith("zh")
            ? "未取得定位权限，可输入店员码"
            : "Location permission denied. Use a staff code.",
        ),
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 30_000 },
    );
  }

  async function submitOrder() {
    if (!cart || !store) return;
    if (
      !presenceToken &&
      !queueTicket &&
      staffCode.trim().length < 6 &&
      !activeOrder
    )
      return setLocationMessage(
        locale.startsWith("zh")
          ? "请先验证位置或输入动态到店码"
          : "Verify your location or enter the dynamic venue code.",
      );
    setBusy(true);
    try {
      let order: Order;
      if (activeOrder) {
        order = await api<Order>(`/api/orders/${activeOrder.id}/additions`, {
          method: "POST",
          body: "{}",
        });
        setToast(
          locale.startsWith("zh")
            ? "加菜已提交，只会打印新增菜品"
            : "Add-on sent. Only new items will print.",
        );
      } else {
        const serviceMode =
          store.type === "full_service" && fulfillment === "dine_in"
            ? "table_service"
            : "self_pickup";
        order = await api<Order>("/api/orders", {
          method: "POST",
          headers: customerToken
            ? { Authorization: `Bearer ${customerToken}` }
            : undefined,
          body: JSON.stringify({
            cartId: cart.id,
            fulfillmentMode: fulfillment,
            serviceMode,
            paymentMethod: "cash",
            customerLocale: locale,
            waiterLanguage: "vi",
            presenceToken,
            staffPresenceCode: staffCode,
            entrySource:
              bootstrap.entry.mode === "queue" ? "queue_qr" : "standard",
            queueTicketId: queueTicket?.id,
          }),
        });
        if (queueTicket)
          localStorage.setItem(`globaldine:preorder:${store.id}`, order.id);
        setToast(
          store.productionTrigger === "cashier_confirmation"
            ? locale.startsWith("zh")
              ? "请到收银台确认后取得取餐号"
              : "Pay at cashier to receive a pickup number"
            : locale.startsWith("zh")
              ? "订单已发送，等待商家接单"
              : "Order sent for merchant acceptance",
        );
      }
      setOrders((current) => [
        order,
        ...current.filter((candidate) => candidate.id !== order.id),
      ]);
      setCheckoutOpen(false);
      setCartMobile(false);
      setView("orders");
    } catch (error) {
      setToast((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function takeQueueNumber() {
    if (!store) return;
    try {
      unlockQueueSpeech();
      const ticket = await api<QueueTicket>("/api/queue/tickets", {
        method: "POST",
        body: JSON.stringify({
          storeId: store.id,
          dinerId: diner.id,
          customerLocale: locale,
          partySize,
          tableType: "any",
          source: "onsite",
        }),
      });
      setQueueTicket(ticket);
      setPresenceToken((current) => current ?? "queue-ticket-proof");
      localStorage.setItem(`globaldine:queue-ticket:${store.id}`, ticket.id);
    } catch (error) {
      setToast((error as Error).message);
    }
  }

  async function submitReview() {
    if (!reviewOpen) return;
    if (!customerToken) {
      setReviewOpen(undefined);
      setAuthOpen(true);
      return;
    }
    try {
      await api("/api/reviews", {
        method: "POST",
        headers: { Authorization: `Bearer ${customerToken}` },
        body: JSON.stringify({
          orderId: reviewOpen.id,
          dinerId: diner.id,
          overall: rating,
          taste: rating,
          service: rating,
          environment: rating,
          value: rating,
          text: reviewText,
          locale,
          anonymous: false,
        }),
      });
      setReviewOpen(undefined);
      setToast(
        locale.startsWith("zh") ? "感谢你的评价" : "Thanks for your review",
      );
    } catch (error) {
      setToast((error as Error).message);
    }
  }

  const AccountControl = () =>
    customer ? (
      <div className="account-control">
        <UserRound size={15} />
        <span>{customer.displayName}</span>
        <button onClick={customerSignOut}>
          {customerAuthCopy[locale].logout}
        </button>
      </div>
    ) : (
      <Button
        variant="secondary"
        className="small"
        onClick={() => setAuthOpen(true)}
      >
        <LogIn size={15} />
        {customerAuthCopy[locale].login} / {customerAuthCopy[locale].register}
      </Button>
    );
  const AuthModal = () =>
    authOpen ? (
      <div className="modal-backdrop">
        <div className="modal" style={{ maxWidth: 430 }}>
          <div className="modal-header">
            <h2>{customerAuthCopy[locale].account}</h2>
            <button className="icon-button" onClick={() => setAuthOpen(false)}>
              <X size={17} />
            </button>
          </div>
          <div className="modal-content stack">
            <div className="segmented">
              <button
                className={authMode === "login" ? "active" : ""}
                onClick={() => setAuthMode("login")}
              >
                {customerAuthCopy[locale].login}
              </button>
              <button
                className={authMode === "register" ? "active" : ""}
                onClick={() => setAuthMode("register")}
              >
                {customerAuthCopy[locale].register}
              </button>
            </div>
            {authMode === "register" && (
              <label className="field">
                <span>{customerAuthCopy[locale].name}</span>
                <input
                  className="input"
                  value={authForm.displayName}
                  onChange={(event) =>
                    setAuthForm({
                      ...authForm,
                      displayName: event.target.value,
                    })
                  }
                />
              </label>
            )}
            <label className="field">
              <span>{customerAuthCopy[locale].identifier}</span>
              <input
                className="input"
                value={authForm.identifier}
                onChange={(event) =>
                  setAuthForm({ ...authForm, identifier: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>{customerAuthCopy[locale].password}</span>
              <input
                className="input"
                type="password"
                minLength={8}
                value={authForm.password}
                onChange={(event) =>
                  setAuthForm({ ...authForm, password: event.target.value })
                }
              />
            </label>
            <p className="muted tiny">
              {customerAuthCopy[locale].loginToReview}
            </p>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={() => setAuthOpen(false)}>
              {locale.startsWith("zh") ? "取消" : "Cancel"}
            </Button>
            <Button
              disabled={
                busy ||
                !authForm.identifier ||
                authForm.password.length < 8 ||
                (authMode === "register" && !authForm.displayName)
              }
              onClick={submitCustomerAuth}
            >
              {authMode === "login"
                ? customerAuthCopy[locale].login
                : customerAuthCopy[locale].register}
            </Button>
          </div>
        </div>
      </div>
    ) : null;

  if (!bootstrap || !store)
    return (
      <div className="customer-app">
        <div className="empty-state">
          <div className="logo-mark">
            <StoreIcon />
          </div>
          <strong>GlobeDine</strong>
          <p>Loading multilingual menu…</p>
        </div>
      </div>
    );
  if (bootstrap.entry.mode === "queue_display")
    return <QueueBoard bootstrap={bootstrap} />;
  if (
    bootstrap.entry.mode === "queue" &&
    view === "queue" &&
    !preorderStarted
  ) {
    const copy = waitPageCopy[locale];
    return (
      <div className="queue-entry">
        <header>
          <Logo />
          <div className="row-actions">
            <AccountControl />
            <select
              className="select"
              value={locale}
              onChange={(event) => changeLocale(event.target.value as Locale)}
            >
              {supportedLocales.map((code) => (
                <option key={code} value={code}>
                  {localeLabels[code]}
                </option>
              ))}
            </select>
          </div>
        </header>
        <main className="waitlist-page">
          <h1>{localize(store.name, locale)}</h1>
          {queueTicket ? (
            <>
              <div className="waitlist-current">
                <span>{copy.current}</span>
                <strong>{queueDisplay?.calledNumbers[0] ?? "—"}</strong>
              </div>
              <section className="waitlist-ticket">
                <p>{copy.yourNumber}</p>
                <strong>{queueTicket.number}</strong>
                {queueTicket.status === "skipped" && (
                  <p className="form-error">{queueExtraCopy[locale].missed}</p>
                )}
                {queueTicket.status !== "skipped" && (
                  <div>
                    <span>
                      {copy.ahead}
                      <b>{queueTicket.aheadCount}</b>
                      {copy.tables}
                    </span>
                    <span>
                      {copy.estimate}
                      <b>{queueTicket.estimatedMinutes}</b>
                      {copy.minutes}
                    </span>
                  </div>
                )}
              </section>
              {queueTicket.status === "skipped" && (
                <Button onClick={takeQueueNumber}>
                  <ListOrdered size={16} />
                  {queueExtraCopy[locale].takeAgain}
                </Button>
              )}
              <button
                className="preorder-button"
                onClick={() => setPreorderStarted(true)}
              >
                <ShoppingBag />
                <strong>{copy.preorder}</strong>
                <span>{copy.preorderHint}</span>
              </button>
            </>
          ) : (
            <>
              <h2>{copy.partySize}</h2>
              <div className="segmented party-selector">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((size) => (
                  <button
                    key={size}
                    className={partySize === size ? "active" : ""}
                    onClick={() => setPartySize(size)}
                  >
                    {size}
                  </button>
                ))}
                <input
                  aria-label={queueExtraCopy[locale].custom}
                  title={queueExtraCopy[locale].custom}
                  className="input compact"
                  type="number"
                  min="1"
                  max="30"
                  placeholder={queueExtraCopy[locale].custom}
                  value={partySize > 8 ? partySize : ""}
                  onChange={(event) =>
                    setPartySize(
                      Math.max(
                        1,
                        Math.min(30, Number(event.target.value) || 1),
                      ),
                    )
                  }
                />
              </div>
              <Button onClick={takeQueueNumber}>
                <ListOrdered size={16} />
                {copy.getNumber}
              </Button>
            </>
          )}
        </main>
        <AuthModal />
        {toast && <div className="toast">{toast}</div>}
      </div>
    );
  }
  if (!cart)
    return (
      <div className="customer-app">
        <div className="empty-state">
          <div className="logo-mark">
            <StoreIcon />
          </div>
          <strong>GlobeDine</strong>
          <p>Loading multilingual menu…</p>
        </div>
      </div>
    );

  if (view === "entry") {
    const copy = queueCopy[locale];
    return (
      <div className="queue-entry">
        <header>
          <Logo />
          <select
            className="select"
            value={locale}
            onChange={(event) => changeLocale(event.target.value as Locale)}
          >
            {supportedLocales.map((code) => (
              <option key={code} value={code}>
                {localeLabels[code]}
              </option>
            ))}
          </select>
        </header>
        <main>
          <p>{copy.welcome}</p>
          <h1>{localize(store.name, locale)}</h1>
          <h2>{copy.choose}</h2>
          <div className="queue-entry-actions">
            <button onClick={() => setView("queue")}>
              <Users />
              <strong>{copy.dine}</strong>
              <span>{copy.dineHint}</span>
            </button>
            {(store.queueTakeawayEnabled ?? true) && (
              <button
                onClick={() => {
                  setFulfillment("takeaway");
                  setView("menu");
                }}
              >
                <ShoppingBag />
                <strong>{copy.take}</strong>
                <span>{copy.takeHint}</span>
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  const Cart = () => (
    <aside className={`cart-panel ${cartMobile ? "mobile-open" : ""}`}>
      <div className="cart-head">
        <div>
          <h2>{t("cart")}</h2>
          <span className="tiny muted">
            {cart.tableId ? `${cart.tableId} · ` : ""}
            {cart.diners.length} diners · synced
          </span>
        </div>
        <div className="diner-avatars">
          {cart.diners.slice(0, 4).map((person) => (
            <span className="avatar" key={person.id} title={person.name}>
              {person.name.slice(0, 1).toUpperCase()}
            </span>
          ))}
        </div>
        {cartMobile && (
          <button className="icon-button" onClick={() => setCartMobile(false)}>
            <X size={17} />
          </button>
        )}
      </div>
      <div className="cart-items">
        {cart.items.length === 0 ? (
          <EmptyState
            icon={<ShoppingBag />}
            title={t("emptyCart")}
            detail={
              locale.startsWith("zh")
                ? "同桌成员加入的菜品会实时出现在这里"
                : "Items from everyone at the table sync here."
            }
          />
        ) : (
          cart.items.map((item) => {
            const dish = store.menu.find(
              (candidate) => candidate.id === item.menuItemId,
            )!;
            const canEdit =
              item.dinerId === diner.id || cart.hostDinerId === diner.id;
            return (
              <div className="cart-item" key={item.id}>
                <div>
                  <h4>{localize(dish.name, locale)}</h4>
                  <p>
                    {item.modifiers
                      .map((modifier) => localize(modifier.name, locale))
                      .join(" · ") ||
                      item.notes ||
                      "—"}
                  </p>
                  <span className="cart-item-owner">
                    {item.dinerName}
                    {item.dinerId === diner.id ? " · you" : ""}
                  </span>
                  {canEdit && (
                    <div className="cart-controls">
                      <button
                        onClick={() =>
                          changeQuantity(item.id, item.quantity - 1)
                        }
                      >
                        <Minus size={12} />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          changeQuantity(item.id, item.quantity + 1)
                        }
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <strong>
                  {formatMoney(
                    cartItemUnitPrice(dish, item) * item.quantity,
                    store.baseCurrency,
                    locale,
                  )}
                </strong>
              </div>
            );
          })
        )}
      </div>
      <div className="cart-total">
        <div className="split">
          <span className="muted">
            {t("total")}
            <small style={{ display: "block" }}>
              {t("approximate")} {formatMoney(approximate, currency, locale)}
            </small>
          </span>
          <strong>{formatMoney(cartTotal, store.baseCurrency, locale)}</strong>
        </div>
        <Button
          disabled={!cart.items.length}
          onClick={() => setCheckoutOpen(true)}
        >
          {activeOrder
            ? locale.startsWith("zh")
              ? "提交加菜"
              : "Send add-on"
            : t("checkout")}
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="customer-app">
      <header className="customer-hero">
        <div className="customer-top">
          <Logo />
          <div className="customer-actions">
            <select
              className="select"
              aria-label="Language"
              value={locale}
              onChange={(event) => changeLocale(event.target.value as Locale)}
            >
              {supportedLocales.map((code) => (
                <option key={code} value={code}>
                  {localeLabels[code]}
                </option>
              ))}
            </select>
            <select
              className="select"
              aria-label="Currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
            >
              {[
                store.baseCurrency,
                "USD",
                "CNY",
                "JPY",
                "KRW",
                "RUB",
                "THB",
                "EUR",
              ]
                .filter((value, index, all) => all.indexOf(value) === index)
                .map((code) => (
                  <option key={code}>{code}</option>
                ))}
            </select>
          </div>
        </div>
        <div className="store-intro">
          <span className="eyebrow">
            <MapPin size={13} /> {store.country} ·{" "}
            {cart.tableId ??
              (locale.startsWith("zh") ? "柜台取餐" : "Counter pickup")}
          </span>
          <h1>{localize(store.name, locale)}</h1>
          <p>{localize(store.description, locale)}</p>
        </div>
      </header>
      <div className="customer-toolbar">
        <nav className="customer-nav">
          <button
            className={view === "menu" ? "active" : ""}
            onClick={() => setView("menu")}
          >
            <StoreIcon size={15} />
            {t("menu")}
          </button>
          <button
            className={view === "orders" ? "active" : ""}
            onClick={() => setView("orders")}
          >
            <ReceiptText size={15} />
            {t("orders")}
            {activeOrder && <Badge tone="amber">1</Badge>}
          </button>
        </nav>
        <AccountControl />
      </div>

      <main className="customer-main">
        {view === "menu" && (
          <div className="customer-grid">
            <section>
              <div className="menu-heading">
                <div>
                  <h2>{t("menu")}</h2>
                  <span className="tiny muted">
                    {store.menu.filter((item) => item.available).length} items ·{" "}
                    {localize(store.name, locale)}
                  </span>
                </div>
                {activeOrder && (
                  <Badge tone="blue">
                    {locale.startsWith("zh")
                      ? "正在用餐 · 可加菜"
                      : "Open table · add dishes"}
                  </Badge>
                )}
              </div>
              <div className="category-pills">
                <button
                  className={category === "all" ? "active" : ""}
                  onClick={() => setCategory("all")}
                >
                  {locale.startsWith("zh") ? "全部" : "All"}
                </button>
                {store.categories
                  .sort((a, b) => a.sort - b.sort)
                  .map((item) => (
                    <button
                      key={item.id}
                      className={category === item.id ? "active" : ""}
                      onClick={() => setCategory(item.id)}
                    >
                      {localize(item.name, locale)}
                    </button>
                  ))}
              </div>
              <div className="menu-grid">
                {menu.map((item) => (
                  <article
                    key={item.id}
                    className={`menu-card ${item.available ? "" : "sold-out"}`}
                  >
                    <div>
                      <h3>{localize(item.name, locale)}</h3>
                      <p>{localize(item.description, locale)}</p>
                      <div className="menu-price">
                        {formatMoney(item.price, store.baseCurrency, locale)}
                        <small>
                          {t("approximate")}{" "}
                          {formatMoney(
                            convertCurrency(
                              item.price,
                              bootstrap.rates,
                              currency,
                            ),
                            currency,
                            locale,
                          )}
                        </small>
                      </div>
                      {item.allergens.length > 0 && (
                        <span className="tiny muted">
                          ⚠ {item.allergens.join(", ")}
                        </span>
                      )}
                    </div>
                    <div className="dish-image">{item.image}</div>
                    {item.available ? (
                      <button
                        className="add-button"
                        aria-label={t("add")}
                        onClick={() => openDish(item)}
                      >
                        +
                      </button>
                    ) : (
                      <Badge tone="red">Sold out</Badge>
                    )}
                  </article>
                ))}
              </div>
            </section>
            <Cart />
          </div>
        )}

        {view === "orders" && (
          <div className="order-tracker">
            {orders.length === 0 ? (
              <EmptyState
                icon={<ReceiptText size={38} />}
                title={locale.startsWith("zh") ? "还没有订单" : "No orders yet"}
                detail={
                  locale.startsWith("zh")
                    ? "下单后，退出再扫码也能恢复订单"
                    : "Your order is restored when you scan again."
                }
              />
            ) : (
              orders.map((order) => (
                <section className="tracker-card" key={order.id}>
                  <div className="split">
                    <div>
                      <Badge tone={orderTone(order.status)}>
                        {statusLabel(order.status, locale)}
                      </Badge>
                      <h2 style={{ margin: "9px 0 2px" }}>
                        {order.displayNumber}
                        {order.tableId ? ` · ${order.tableId}` : ""}
                      </h2>
                      <span className="tiny muted">
                        {new Date(order.createdAt).toLocaleTimeString(locale, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        · {order.items.length} items
                      </span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {order.pickupNumber ? (
                        <>
                          <span className="tiny muted">Pickup</span>
                          <div className="pickup-number">
                            {order.pickupNumber}
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="tiny muted">
                            {locale.startsWith("zh")
                              ? "当前合计"
                              : "Order total"}
                          </span>
                          <h2>
                            {formatMoney(order.total, order.currency, locale)}
                          </h2>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="timeline">
                    {["sent", "accepted", "preparing", "ready", "done"].map(
                      (step, index) => (
                        <div
                          key={step}
                          className={`timeline-step ${index <= (statusIndex[order.status] ?? 0) ? "done" : ""}`}
                        >
                          {step}
                        </div>
                      ),
                    )}
                  </div>
                  <div style={{ marginTop: 19 }} className="stack">
                    {order.items.map((item) => (
                      <div className="split" key={item.orderItemId}>
                        <span>
                          <strong>{item.quantity}×</strong>{" "}
                          {localize(item.nameSnapshot, locale)}{" "}
                          {item.batchNumber > 1 && (
                            <Badge tone="purple">
                              + batch {item.batchNumber}
                            </Badge>
                          )}
                        </span>
                        <Badge tone={orderTone(item.status)}>
                          {statusLabel(item.status, locale)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 17 }}>
                    {!["completed", "cancelled"].includes(order.status) && (
                      <Button
                        variant="secondary"
                        onClick={() => setView("menu")}
                      >
                        <Plus size={15} />
                        {locale.startsWith("zh") ? "继续加菜" : "Add dishes"}
                      </Button>
                    )}
                    {["served", "completed"].includes(order.status) && (
                      <Button
                        variant="secondary"
                        onClick={() => setReviewOpen(order)}
                      >
                        <Star size={15} />
                        {t("review")}
                      </Button>
                    )}
                  </div>
                </section>
              ))
            )}
          </div>
        )}

        {view === "queue" && (
          <div className="order-tracker">
            <section
              className="tracker-card"
              style={{ textAlign: "center", padding: 30 }}
            >
              {queueTicket ? (
                <>
                  <Badge
                    tone={queueTicket.status === "called" ? "green" : "amber"}
                  >
                    {statusLabel(queueTicket.status, locale)}
                  </Badge>
                  <p className="muted" style={{ margin: "15px 0 2px" }}>
                    {locale.startsWith("zh")
                      ? "你的排队号码"
                      : "Your waitlist number"}
                  </p>
                  <div className="pickup-number" style={{ fontSize: 66 }}>
                    {queueTicket.number}
                  </div>
                  <h3>
                    {queueTicket.aheadCount}{" "}
                    {locale.startsWith("zh")
                      ? "桌在前 · 预计"
                      : "parties ahead · about"}{" "}
                    {queueTicket.estimatedMinutes} min
                  </h3>
                  <p className="muted">
                    <BellRing size={14} style={{ verticalAlign: -2 }} />{" "}
                    {locale.startsWith("zh")
                      ? "临近叫号时请回到餐厅附近"
                      : "Return near the restaurant before you are called."}
                  </p>
                </>
              ) : (
                <>
                  <div className="logo-mark" style={{ margin: "0 auto 15px" }}>
                    <Users />
                  </div>
                  <h2>
                    {locale.startsWith("zh")
                      ? "餐厅排队取号"
                      : "Join the restaurant waitlist"}
                  </h2>
                  <p className="muted">
                    {locale.startsWith("zh")
                      ? "排队号 Q 和取餐号 P 完全独立，避免混淆"
                      : "Waitlist Q numbers stay separate from pickup P numbers."}
                  </p>
                  <div
                    className="field"
                    style={{ maxWidth: 220, margin: "20px auto" }}
                  >
                    <label>
                      {locale.startsWith("zh") ? "用餐人数" : "Party size"}
                    </label>
                    <div className="segmented" style={{ margin: "auto" }}>
                      {[1, 2, 3, 4, 5, 6].map((size) => (
                        <button
                          key={size}
                          className={partySize === size ? "active" : ""}
                          onClick={() => setPartySize(size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={takeQueueNumber}>
                    <ListOrdered size={16} />
                    {t("joinQueue")}
                  </Button>
                </>
              )}
            </section>
          </div>
        )}
      </main>

      {cart.items.length > 0 && view === "menu" && (
        <div className="mobile-cart-bar" onClick={() => setCartMobile(true)}>
          <div>
            <strong>
              {cart.items.reduce((sum, item) => sum + item.quantity, 0)}{" "}
              {locale.startsWith("zh") ? "份" : "items"}
            </strong>
            <span className="tiny" style={{ display: "block", opacity: 0.7 }}>
              {formatMoney(cartTotal, store.baseCurrency, locale)}
            </span>
          </div>
          <Button>{t("cart")}</Button>
        </div>
      )}

      {selected && (
        <div
          className="modal-backdrop"
          onMouseDown={() => setSelected(undefined)}
        >
          <div
            className="modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>{localize(selected.name, locale)}</h2>
                <span className="muted tiny">
                  {formatMoney(selected.price, store.baseCurrency, locale)}
                </span>
              </div>
              <button
                className="icon-button"
                onClick={() => setSelected(undefined)}
              >
                <X size={17} />
              </button>
            </div>
            <div className="modal-content">
              <div
                className="dish-image"
                style={{
                  width: "100%",
                  height: 130,
                  fontSize: 72,
                  marginBottom: 18,
                }}
              >
                {selected.image}
              </div>
              {selected.modifierGroups.map((group) => (
                <div className="modifier-group" key={group.id}>
                  <h3>
                    {localize(group.name, locale)}{" "}
                    {group.required && <Badge tone="amber">required</Badge>}
                  </h3>
                  <div className="option-list">
                    {group.options.map((option) => {
                      const checked = selectedModifiers.some(
                        (modifier) => modifier.optionId === option.id,
                      );
                      return (
                        <button
                          key={option.id}
                          className={`option ${checked ? "selected" : ""}`}
                          onClick={() =>
                            chooseModifier(selected, group.id, option.id)
                          }
                        >
                          <span>
                            {checked ? <Check size={14} /> : null}{" "}
                            {localize(option.name, locale)}
                          </span>
                          <span>
                            {option.priceDelta
                              ? `+${formatMoney(option.priceDelta, store.baseCurrency, locale)}`
                              : ""}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="field">
                <label>
                  {locale.startsWith("zh")
                    ? "给后厨的备注（自动保留客户原文）"
                    : "Note for kitchen"}
                </label>
                <textarea
                  className="textarea"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={
                    locale.startsWith("zh")
                      ? "例如：不要香菜"
                      : "e.g. no cilantro"
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => setSelected(undefined)}
              >
                {locale.startsWith("zh") ? "取消" : "Cancel"}
              </Button>
              <Button disabled={busy} onClick={addDish}>
                <ShoppingBag size={16} />
                {t("add")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {checkoutOpen && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <div>
                <h2>
                  {activeOrder
                    ? locale.startsWith("zh")
                      ? "确认加菜"
                      : "Confirm add-on"
                    : t("checkout")}
                </h2>
                <span className="muted tiny">
                  {store.productionTrigger === "cashier_confirmation"
                    ? t("cashier")
                    : locale.startsWith("zh")
                      ? "商家接单后进入制作"
                      : "Production starts after merchant acceptance"}
                </span>
              </div>
              <button
                className="icon-button"
                onClick={() => setCheckoutOpen(false)}
              >
                <X size={17} />
              </button>
            </div>
            <div className="modal-content">
              <div
                className="panel-body"
                style={{
                  background: "#f4f1e9",
                  borderRadius: 13,
                  marginBottom: 17,
                }}
              >
                <div className="split">
                  <span>{t("total")}</span>
                  <strong>
                    {formatMoney(cartTotal, store.baseCurrency, locale)}
                  </strong>
                </div>
                <div className="split tiny muted" style={{ marginTop: 5 }}>
                  <span>{t("approximate")} · rate snapshot</span>
                  <span>{formatMoney(approximate, currency, locale)}</span>
                </div>
              </div>
              {!activeOrder && (
                <>
                  <h3 style={{ fontSize: 14 }}>{t("location")}</h3>
                  <p className="muted tiny">
                    {locale.startsWith("zh")
                      ? "菜单可以远程浏览，但提交订单必须证明你在餐厅附近。只保留验证结果，不长期保存精确坐标。"
                      : "Browse remotely, but ordering requires venue presence. Precise coordinates are not retained."}
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button variant="secondary" onClick={verifyLocation}>
                      <LocateFixed size={16} />
                      GPS
                    </Button>
                    {bootstrap.runtime.demo && (
                      <Button
                        variant="ghost"
                        onClick={() =>
                          verifyCoordinates(
                            store.location.lat,
                            store.location.lng,
                            12,
                          )
                        }
                      >
                        Demo: at venue
                      </Button>
                    )}
                  </div>
                  {locationMessage && (
                    <p
                      className="tiny"
                      style={{
                        marginTop: 10,
                        color: presenceToken ? "var(--brand)" : "var(--coral)",
                      }}
                    >
                      {presenceToken && (
                        <Check size={13} style={{ verticalAlign: -2 }} />
                      )}{" "}
                      {locationMessage}
                    </p>
                  )}
                  {bootstrap.entry.staffPresenceCodeEnabled && (
                    <div className="field" style={{ marginTop: 15 }}>
                      <label>
                        {locale.startsWith("zh")
                          ? `无法定位？输入店员到店码${bootstrap.runtime.demo ? "（测试：2468）" : ""}`
                          : `No GPS? Enter the staff presence code${bootstrap.runtime.demo ? " (test: 2468)" : ""}`}
                      </label>
                      <input
                        className="input"
                        maxLength={8}
                        inputMode="numeric"
                        value={staffCode}
                        onChange={(event) => setStaffCode(event.target.value)}
                        placeholder="••••"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => setCheckoutOpen(false)}
              >
                {locale.startsWith("zh") ? "返回" : "Back"}
              </Button>
              <Button
                disabled={
                  busy ||
                  (!activeOrder &&
                    !presenceToken &&
                    staffCode.trim().length < 4)
                }
                onClick={submitOrder}
              >
                {busy
                  ? "…"
                  : activeOrder
                    ? locale.startsWith("zh")
                      ? "发送新增菜品"
                      : "Send new items"
                    : t("checkout")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reviewOpen && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>
                {t("review")} · {reviewOpen.displayNumber}
              </h2>
              <button
                className="icon-button"
                onClick={() => setReviewOpen(undefined)}
              >
                <X size={17} />
              </button>
            </div>
            <div className="modal-content" style={{ textAlign: "center" }}>
              <p className="muted">
                {locale.startsWith("zh")
                  ? "只有已完成订单可以评价，每位同桌客人一次"
                  : "Verified diners can review once per completed order."}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 7,
                  margin: "20px 0",
                }}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    className="icon-button"
                    style={{ color: value <= rating ? "#efad3d" : "#bbb" }}
                    onClick={() => setRating(value)}
                  >
                    <Star fill={value <= rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              <textarea
                className="textarea"
                style={{ width: "100%" }}
                value={reviewText}
                onChange={(event) => setReviewText(event.target.value)}
                placeholder={
                  locale.startsWith("zh")
                    ? "味道、服务或环境如何？"
                    : "How was the food and service?"
                }
              />
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                onClick={() => setReviewOpen(undefined)}
              >
                Cancel
              </Button>
              <Button onClick={submitReview}>
                <MessageSquareText size={16} />
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
      <AuthModal />
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
